export const PARTNER_COLLABORATION_SYNC_EVENT =
  "maya-partner-collaboration-sync";

export const PARTNER_COLLABORATION_STORAGE_KEY = "partnerCollaboration";

const DEFAULT_PARTNER_COLLABORATION_STATE = {
  enabled: false,
  profileSharingEnabled: false,
  jobRoleSharingEnabled: false,
};

const INTERNAL_EVENT_SOURCE = "partner-collaboration-state-helper";

const partnerCollaborationListeners = new Set();

/**
 * Converts session/API values into actual booleans.
 *
 * Supported values:
 * true, false
 * "true", "false"
 * 1, 0
 * "1", "0"
 */
const normalizeBoolean = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();

    return ["true", "1", "yes", "enabled"].includes(normalizedValue);
  }

  return false;
};

/**
 * Converts either:
 *
 * 1. A boolean:
 *    true
 *
 * 2. A JSON object:
 *    {
 *      enabled: true,
 *      profileSharingEnabled: true,
 *      jobRoleSharingEnabled: false
 *    }
 *
 * 3. An API-style object:
 *    {
 *      partnerCollaboration: true,
 *      profileShare: true,
 *      jobRolesShare: false
 *    }
 *
 * into one consistent state object.
 */
const normalizePartnerCollaborationState = (value) => {
  /*
   * Backward-compatible support when session storage contains:
   *
   * partnerCollaboration = "true"
   * partnerCollaboration = "false"
   */
  if (
    typeof value === "boolean" ||
    typeof value === "string" ||
    typeof value === "number"
  ) {
    const enabled = normalizeBoolean(value);

    return {
      enabled,
      profileSharingEnabled: enabled,
      jobRoleSharingEnabled: enabled,
    };
  }

  if (!value || typeof value !== "object") {
    return {
      ...DEFAULT_PARTNER_COLLABORATION_STATE,
    };
  }

  const profileSharingEnabled = normalizeBoolean(
    value.profileSharingEnabled ??
      value.profileShare ??
      value.shareProfiles ??
      false,
  );

  const jobRoleSharingEnabled = normalizeBoolean(
    value.jobRoleSharingEnabled ??
      value.jobRolesShare ??
      value.shareJobRoles ??
      false,
  );

  const explicitEnabledValue =
    value.enabled ?? value.partnerCollaboration ?? value.isEnabled;

  /*
   * This fallback is retained for backward compatibility when a complete
   * state object is normalized without an explicit organization-level flag.
   * Partial object updates are merged with the stored state before reaching
   * this function, so capability-only writes preserve the existing `enabled`.
   */
  const enabled =
    explicitEnabledValue !== undefined && explicitEnabledValue !== null
      ? normalizeBoolean(explicitEnabledValue)
      : profileSharingEnabled || jobRoleSharingEnabled;

  return {
    enabled,
    profileSharingEnabled,
    jobRoleSharingEnabled,
  };
};

/**
 * Reads the latest value directly from sessionStorage.
 */
const loadPartnerCollaborationStateFromStorage = () => {
  if (typeof window === "undefined") {
    return {
      ...DEFAULT_PARTNER_COLLABORATION_STATE,
    };
  }

  try {
    const storedValue = window.sessionStorage.getItem(
      PARTNER_COLLABORATION_STORAGE_KEY,
    );

    if (storedValue === null) {
      return {
        ...DEFAULT_PARTNER_COLLABORATION_STATE,
      };
    }

    try {
      /*
       * Supports JSON values such as:
       *
       * {
       *   "enabled": true,
       *   "profileSharingEnabled": true,
       *   "jobRoleSharingEnabled": false
       * }
       *
       * JSON.parse also correctly handles "true" and "false".
       */
      const parsedValue = JSON.parse(storedValue);

      return normalizePartnerCollaborationState(parsedValue);
    } catch {
      /*
       * Supports non-JSON legacy values stored as plain strings.
       */
      return normalizePartnerCollaborationState(storedValue);
    }
  } catch (error) {
    console.error(
      "Unable to read partner collaboration from sessionStorage:",
      error,
    );

    return {
      ...DEFAULT_PARTNER_COLLABORATION_STATE,
    };
  }
};

/**
 * Initialize the module state using sessionStorage.
 */
let currentPartnerCollaborationState =
  loadPartnerCollaborationStateFromStorage();

const notifyPartnerCollaborationListeners = (state) => {
  partnerCollaborationListeners.forEach((listener) => {
    listener({
      ...state,
    });
  });
};

const dispatchPartnerCollaborationSyncEvent = (state) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(PARTNER_COLLABORATION_SYNC_EVENT, {
      detail: {
        source: INTERNAL_EVENT_SOURCE,
        state: {
          ...state,
        },
      },
    }),
  );
};

/**
 * Always returns the latest sessionStorage value.
 */
export const readPartnerCollaborationState = () => {
  currentPartnerCollaborationState = loadPartnerCollaborationStateFromStorage();

  return {
    ...currentPartnerCollaborationState,
  };
};

/**
 * Updates both:
 *
 * 1. sessionStorage
 * 2. React subscribers
 *
 * This function can accept either an object or a boolean.
 * Object updates are treated as partial updates and preserve fields that were
 * not supplied by the caller.
 */
export const writePartnerCollaborationState = (updates = {}) => {
  const storedState = loadPartnerCollaborationStateFromStorage();

  let nextState;

  if (
    typeof updates === "boolean" ||
    typeof updates === "string" ||
    typeof updates === "number"
  ) {
    nextState = normalizePartnerCollaborationState(updates);
  } else {
    nextState = normalizePartnerCollaborationState({
      ...storedState,
      ...updates,
    });
  }

  currentPartnerCollaborationState = nextState;

  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(
        PARTNER_COLLABORATION_STORAGE_KEY,
        JSON.stringify(nextState),
      );
    } catch (error) {
      console.error(
        "Unable to save partner collaboration to sessionStorage:",
        error,
      );
    }
  }

  notifyPartnerCollaborationListeners(nextState);
  dispatchPartnerCollaborationSyncEvent(nextState);

  return {
    ...nextState,
  };
};

export const subscribePartnerCollaborationState = (callback) => {
  if (typeof callback !== "function") {
    return () => {};
  }

  partnerCollaborationListeners.add(callback);

  /*
   * Read sessionStorage again when the component subscribes.
   * This prevents stale module values after navigation or refresh.
   */
  callback(readPartnerCollaborationState());

  const handleStorageChange = (event) => {
    if (event.key !== PARTNER_COLLABORATION_STORAGE_KEY) {
      return;
    }

    const latestState = loadPartnerCollaborationStateFromStorage();

    currentPartnerCollaborationState = latestState;

    callback({
      ...latestState,
    });
  };

  const handleManualSyncEvent = (event) => {
    /*
     * writePartnerCollaborationState already notified the subscriber directly.
     * Ignore its own event to avoid updating the component twice.
     */
    if (event?.detail?.source === INTERNAL_EVENT_SOURCE) {
      return;
    }

    const latestState =
      event?.detail?.state !== undefined
        ? normalizePartnerCollaborationState(event.detail.state)
        : loadPartnerCollaborationStateFromStorage();

    currentPartnerCollaborationState = latestState;

    callback({
      ...latestState,
    });
  };

  if (typeof window !== "undefined") {
    window.addEventListener("storage", handleStorageChange);

    window.addEventListener(
      PARTNER_COLLABORATION_SYNC_EVENT,
      handleManualSyncEvent,
    );
  }

  return () => {
    partnerCollaborationListeners.delete(callback);

    if (typeof window !== "undefined") {
      window.removeEventListener("storage", handleStorageChange);

      window.removeEventListener(
        PARTNER_COLLABORATION_SYNC_EVENT,
        handleManualSyncEvent,
      );
    }
  };
};

export const resetPartnerCollaborationState = () => {
  currentPartnerCollaborationState = {
    ...DEFAULT_PARTNER_COLLABORATION_STATE,
  };

  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(PARTNER_COLLABORATION_STORAGE_KEY);
    } catch (error) {
      console.error(
        "Unable to clear partner collaboration from sessionStorage:",
        error,
      );
    }
  }

  notifyPartnerCollaborationListeners(currentPartnerCollaborationState);

  dispatchPartnerCollaborationSyncEvent(currentPartnerCollaborationState);
};

/**
 * Derives only partner-dependent sharing capabilities.
 *
 * Important: this function intentionally does not return the organization-level
 * `enabled` value. Partner lists can be filtered, searched, or paginated, so
 * their contents are not a reliable source for whether Partner Collaboration
 * is enabled for the organization.
 */
export const derivePartnerSharingCapabilities = (partners = []) => {
  const safePartners = Array.isArray(partners) ? partners : [];

  const activePartners = safePartners.filter((partner) => {
    const normalizedStatus = String(
      partner.status || partner.connectionStatus || partner.partnerStatus || "",
    )
      .trim()
      .toLowerCase();

    return ["active", "accepted", "connected"].includes(normalizedStatus);
  });

  const profileSharingEnabled = activePartners.some(
    (partner) =>
      normalizeBoolean(partner.shareProfiles) ||
      normalizeBoolean(partner.profileShare),
  );

  const jobRoleSharingEnabled = activePartners.some(
    (partner) =>
      normalizeBoolean(partner.shareJobRoles) ||
      normalizeBoolean(partner.jobRolesShare),
  );

  return {
    profileSharingEnabled,
    jobRoleSharingEnabled,
  };
};
